/**
 * Centralized React Query key factories.
 * Use these in useQuery/useMutation and invalidateQueries so cache behavior
 * is predictable and invalidation stays in sync.
 *
 * @see docs/codebase_optimization_plan.md Section 4.1 & 8.2
 */

export const queryKeys = {
  // --- Campaign ---
  campaign: (campaignId: string | number) => ['campaign', campaignId] as const,
  campaignNpcs: (campaignId: string | number, filters?: Record<string, unknown>) =>
    ['campaign', campaignId, 'npcs', filters ?? {}] as const,
  campaignNpcsList: (campaignId: string | number) =>
    ['campaign', campaignId, 'npcs'] as const,
  campaignLocations: (campaignId: string | number) =>
    ['campaign', campaignId, 'locations'] as const,
  campaignQuests: (campaignId: string | number, filters?: Record<string, unknown>) =>
    ['campaign', campaignId, 'quests', filters ?? {}] as const,
  campaignQuestsList: (campaignId: string | number) =>
    ['campaign', campaignId, 'quests'] as const,
  campaignShops: (campaignId: string | number, locationId?: string | null) =>
    locationId
      ? (['campaign', campaignId, 'shops', { locationId: String(locationId) }] as const)
      : (['campaign', campaignId, 'shops'] as const),
  campaignSessionSurfaces: (campaignId: string | number) =>
    ['campaign', campaignId, 'session-surfaces'] as const,
  campaignShopOffers: (campaignId: string | number) =>
    ['campaign', campaignId, 'shop-offers'] as const,
  campaignFactions: (campaignId: string | number) =>
    ['campaign', campaignId, 'factions'] as const,
  campaignCharacters: (campaignId: string | number) =>
    ['campaign', campaignId, 'characters'] as const,
  campaignMembers: (campaignId: string | number) =>
    ['campaign', campaignId, 'members'] as const,
  campaignEncounters: (campaignId: string | number) =>
    ['campaign', campaignId, 'encounters'] as const,
  campaignSessionNotes: (campaignId: string | number) =>
    ['campaign', campaignId, 'sessionNotes'] as const,
  campaignDmNotes: (campaignId: string | number) =>
    ['campaign', campaignId, 'dm-notes'] as const,
  campaignTimeline: (campaignId: string | number) =>
    ['campaign', campaignId, 'timeline'] as const,
  campaignActivity: (campaignId: string | number) =>
    ['campaign', campaignId, 'activity'] as const,
  campaignSessions: (campaignId: string | number, filters?: Record<string, unknown>) =>
    ['campaign', campaignId, 'sessions', filters ?? {}] as const,
  shopInventory: (campaignId: string | number, shopId: string | number) =>
    ['campaign', campaignId, 'shops', shopId, 'inventory'] as const,
  campaignSafetySignals: (campaignId: string | number) =>
    ['campaign', campaignId, 'safety-signals'] as const,
  campaignCombats: (campaignId: string | number, activeOnly?: boolean) =>
    ['campaign', campaignId, 'combats', activeOnly ? 'active' : 'all'] as const,
  partyResources: (campaignId: string | number) =>
    ['campaign', campaignId, 'party-resources'] as const,
  lootPools: (campaignId: string | number) =>
    ['campaign', campaignId, 'loot-pools'] as const,
  /** Omit scope so invalidateQueries matches all stash-request caches. */
  stashRequests: (campaignId: string | number, scope?: string) =>
    scope
      ? (['campaign', campaignId, 'stash-requests', scope] as const)
      : (['campaign', campaignId, 'stash-requests'] as const),
  campaignChat: (campaignId: string | number) =>
    ['campaign', campaignId, 'chat'] as const,
  campaignPresence: (campaignId: string | number) =>
    ['campaign', campaignId, 'presence'] as const,
  campaignMaps: (campaignId: string | number) =>
    ['campaign', campaignId, 'maps'] as const,
  campaignMap: (campaignId: string | number, mapId: string | number) =>
    ['campaign', campaignId, 'maps', String(mapId)] as const,

  // --- Character ---
  character: (characterId: string | number, campaignId?: string | number | null) =>
    ['character', String(characterId), campaignId != null && String(campaignId) !== '' ? String(campaignId) : ''] as const,
  characters: () => ['character'] as const,

  // --- Encounter ---
  encounter: (campaignId: string | number, encounterId: string | number) =>
    ['encounter', String(campaignId), String(encounterId)] as const,

  // --- XP / awards ---
  characterXp: (characterId: string | number) =>
    ['character', characterId, 'xp'] as const,
  /** Server-backed journal when viewing a character in a campaign (`?campaignId=`). */
  characterJournal: (characterId: string | number, campaignId: string | number) =>
    ['character-journal', String(characterId), String(campaignId)] as const,
  xpAwards: (campaignId: string | number) =>
    ['campaign', campaignId, 'xpAwards'] as const,

  // --- User workspace (home dashboard) ---
  userWorkspace: () => ['user-workspace'] as const,
} as const;

export type QueryKeys = typeof queryKeys;
